import {
  createEffect,
  createMemo,
  createSignal,
  on,
  onCleanup,
} from "solid-js";

import { isMobile } from "@/lib/isMobile";
import { getFormatImgSrc, getFormatWidthImgSrc } from "@/lib/utils";

// 图片缓存，存储已加载的图片
const imageCache = new Map<string, boolean>();

// 默认的占位图片，一个1x1像素的 #242626 色值纯色图片
const DEFAULT_PLACEHOLDER_ERR =
  "data:image/gif;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVQImWNgYGBgAAAABQABh6FO1AAAAABJRU5ErkJggg==";
const DEFAULT_PLACEHOLDER =
  "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMSIgaGVpZ2h0PSIxIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIxIiBoZWlnaHQ9IjEiIGZpbGw9IiMyNDI2MjYiLz48L3N2Zz4=";

type LazyImgProps = {
  src?: string;
  errorSrc?: string;
  loadingSrc?: string; // 不再使用
  alt?: string;
  class?: string;
  width?: number;
  height?: number;
  loading?: "lazy" | "eager";
  compress?: boolean;
  sizes?: string;
  style?: Record<string, string>;
  onError?: (value: boolean) => void;
  onLoad?: (value: boolean) => void;
  onClick?: (e: MouseEvent | KeyboardEvent) => void;
};

export default function LazyImg(props: LazyImgProps) {
  let imgRef: HTMLImageElement | undefined;
  const [isLoaded, setIsLoaded] = createSignal(false);
  const [currentSrc, setCurrentSrc] = createSignal(
    props.loadingSrc || DEFAULT_PLACEHOLDER,
  );

  // 设置默认值
  const compress = props.compress ?? true;

  // 清理函数
  const cleanup = () => {
    if (imgRef) {
      imgRef.onload = null;
      imgRef.onerror = null;
      imgRef.src = DEFAULT_PLACEHOLDER;
      imgRef = undefined;
    }
    setIsLoaded(false);
    setCurrentSrc(props.loadingSrc || DEFAULT_PLACEHOLDER);
  };

  createEffect(
    on(
      createMemo(() => [imgRef, props.src] as const),
      // createMemo(() => JSON.stringify(!imgRef || !props.src)),
      () => {
        if (!imgRef || !props.src) return;

        // 重置加载状态
        setIsLoaded(false);
        setCurrentSrc(props.loadingSrc || DEFAULT_PLACEHOLDER);

        const loadImage = () => {
          if (!imgRef || !props.src) return;

          const preLoadImageSrc = compress
            ? isMobile()
              ? getFormatWidthImgSrc(props.src, props.width || 0)
              : getFormatImgSrc(props.src)
            : props.src;
          if (!preLoadImageSrc) return;

          // 检查缓存，如果已经加载过，直接设置
          if (imageCache.has(preLoadImageSrc)) {
            imgRef.src = preLoadImageSrc;
            setIsLoaded(true);
            props.onLoad?.(false);
            return;
          }

          const preLoadImage = new Image();
          const safeImgRef = imgRef;
          preLoadImage.onload = () => {
            // 缓存已加载的图片
            imageCache.set(preLoadImageSrc, true);

            // 直接设置 src，让浏览器处理加载
            safeImgRef.src = preLoadImageSrc;

            // 使用 queueMicrotask 确保在下一个微任务中检查加载状态
            queueMicrotask(() => {
              if (imgRef?.complete) {
                setIsLoaded(true);
                props.onLoad?.(false);
              }
            });
          };

          preLoadImage.onerror = () => {
            safeImgRef.src = props.errorSrc || DEFAULT_PLACEHOLDER_ERR;
            props.onError?.(true);
          };

          preLoadImage.src = preLoadImageSrc;
        };

        if (props.loading === "eager") {
          loadImage();
          return;
        }

        // 使用 IntersectionObserver 进行懒加载
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                loadImage();
                observer.disconnect();
              }
            });
          },
          {
            rootMargin: "50px",
            threshold: 0.1,
          },
        );

        observer.observe(imgRef);

        onCleanup(() => {
          observer.disconnect();
        });
      },
    ),
  );

  // 组件卸载时清理
  onCleanup(() => {
    cleanup();
  });

  return (
    <img
      ref={(el) => (imgRef = el)}
      src={currentSrc()}
      alt={props.alt || ""}
      width={props.width}
      height={props.height}
      sizes={props.sizes}
      loading={props.loading}
      decoding="async"
      onLoad={() => {
        setIsLoaded(true);
        // 确保缓存状态正确
        if (props.src) {
          const preLoadImageSrc = compress
            ? isMobile()
              ? getFormatWidthImgSrc(props.src, props.width || 0)
              : getFormatImgSrc(props.src)
            : props.src;
          if (preLoadImageSrc) {
            imageCache.set(preLoadImageSrc, true);
          }
        }
        props.onLoad?.(false);
      }}
      onError={() => {
        if (imgRef) {
          imgRef.src = props.errorSrc || DEFAULT_PLACEHOLDER_ERR;
          props.onError?.(true);
        }
      }}
      class={props.class}
      style={{
        ...props.style,
        // opacity: isLoaded() ? 1 : 0,
        // transition: "background-color 0.3s ease-in-out",
      }}
      onClick={props.onClick}
    />
  );
}
